import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { formatDate, formatCurrency } from '@apply/ui';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { CommonLoanSummary } from '../common/LoanComponents';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { LoanStatusOptions } from '@apply/constants';
import { LoanStatusType } from '../common/LoanConstants';
import DateBox from 'devextreme-react/date-box';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const TerminateMasterNoteModal = () => {
    const { isOpen, loanId } = useSelector((state) => state.loan.terminateMasterNoteModal);
    const { dispatch } = store;

    const [loan, set_loan] = useState(null);
    const [terminationDate, set_terminationDate] = useState(new Date());
    const [notes, set_notes] = useState('');

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { paddingLeft: '5px', paddingTop: '10px' },
        formRow: { marginBottom: '0' },
    };

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setTerminateMasterNoteModal({
                terminateMasterNoteModal: { isOpen: false },
            })
        );

    useEffect(() => {
        resetState();

        async function loadAsync() {
            if (loanId && loanId > 0) {
                await loadMasterNote(loanId);
            }
        }

        loadAsync();
    }, [isOpen, loanId]);

    const resetState = () => {
        set_loadingOpen(false);
        set_loan(null);
        set_terminationDate(new Date());
        set_notes('');
    };

    const loadMasterNote = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await loanApi.getMasterNoteTerminate(id);

            if (response.statusCode !== 200) {
                return;
            }

            const loan = response.result;
            set_loan(loan);
        } finally {
            set_loadingOpen(false);
        }
    };

    const terminateMasterNote = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: loanId,
                terminationDate: terminationDate.toLocaleDateString('en-us'),
                notes,
            };

            const response = await loanApi.setMasterNoteTerminate(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan has been terminated successfully');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const onDateRender = (e) => {
        return (
            <>
                {!e.value && ''}
                {e.value && formatDate(e.value)}{' '}
            </>
        );
    };

    const onCurrencyRender = (e) => {
        return (
            <>
                {!e.value && ''}
                {e.value && formatCurrency(e.value, 0)}{' '}
            </>
        );
    };

    const onLoanStatusRender = (e) => {
        const text = LoanStatusOptions.find((s) => s.id === e.value)?.name;
        if (e.value === LoanStatusType.Outstanding) {
            return <span style={{ color: 'darkred' }}>{text}</span>;
        } else if (e.value === LoanStatusType.Approved) {
            return <span style={{ color: 'darkgreen' }}>{text}</span>;
        } else {
            return text;
        }
    };

    const hasStatusInSubnotes = (subNotes, loanType) => {
        if (!loan) {
            return false;
        }
        const hasOutStanding = subNotes.some((s) => s.statusId === loanType);
        return hasOutStanding;
    };

    const canTerminate = () => {
        if (!loan) {
            return false;
        }
        //Can't terminate unless Outstanding and no Outstanding subnotes
        return loan.statusId === LoanStatusType.Outstanding && !hasStatusInSubnotes(loan.subNotes, LoanStatusType.Outstanding);
    };

    if (!loanId || !loan) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '750px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='receiveAppraisalForm'
                                    onSubmit={(event) => terminateMasterNote(event)}
                                >
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Terminate Master Note</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <CommonLoanSummary
                                                    loan={loan}
                                                    invokingFeature={CommonLoanSummaryInvokingFeatureType.TerminateMasterNote}
                                                />
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <DataGrid
                                                            dataSource={loan.subNotes}
                                                            showBorders={true}
                                                            showRowLines={false}
                                                            rowAlternationEnabled={true}
                                                            remoteOperations={false}
                                                            allowColumnReordering={false}
                                                            allowColumnResizing={true}
                                                            width={'100%'}
                                                        >
                                                            <Column
                                                                dataField='bfiNumber'
                                                                caption='BFI #'
                                                                width={120}
                                                                alignment={'left'}
                                                            />
                                                            <Column
                                                                dataField='statusId'
                                                                caption='Status'
                                                                alignment={'left'}
                                                                cellRender={onLoanStatusRender}
                                                            />
                                                            <Column
                                                                dataField='closingDate'
                                                                caption='Closing Date'
                                                                width={100}
                                                                alignment={'left'}
                                                                cellRender={onDateRender}
                                                            />
                                                            <Column
                                                                dataField='maturityDate'
                                                                caption='Maturity Date'
                                                                width={100}
                                                                alignment={'left'}
                                                                cellRender={onDateRender}
                                                            />
                                                            <Column
                                                                dataField='commitment'
                                                                caption='Commitment'
                                                                width={100}
                                                                alignment={'left'}
                                                                cellRender={onCurrencyRender}
                                                            />
                                                        </DataGrid>
                                                        {hasStatusInSubnotes(loan.subNotes, LoanStatusType.Outstanding) && (
                                                            <div
                                                                style={{
                                                                    color: 'darkred',
                                                                    marginTop: '15px',
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                &nbsp; MasterNote contains Outstanding SubNotes. It can only be
                                                                terminated if all SubNotes are Approved, Repaid, Lost or Deleted
                                                            </div>
                                                        )}
                                                        {!hasStatusInSubnotes(loan.subNotes, LoanStatusType.Outstanding) &&
                                                            hasStatusInSubnotes(loan.subNotes, LoanStatusType.Approved) && (
                                                                <div
                                                                    style={{
                                                                        color: 'darkgreen',
                                                                        marginTop: '15px',
                                                                    }}
                                                                >
                                                                    Note: Any <b>Approved</b> SubNotes will be changed to{' '}
                                                                    <b>Deleted</b> upon Termination of the MasterNote.
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='approvalDate'>
                                                            Termination Date
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <DateBox
                                                            id='terminationDate'
                                                            value={terminationDate}
                                                            onValueChange={set_terminationDate}
                                                            required={true}
                                                            disabled={!canTerminate()}
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='notes'>
                                                            Notes
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <textarea
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                            rows={3}
                                                            id='notes'
                                                            value={notes}
                                                            onChange={(e) => set_notes(e.target.value)}
                                                            required={true}
                                                            placeholder='Enter further description and explanation notes...'
                                                            maxLength={1000}
                                                            disabled={!canTerminate()}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ padding: '5px 5px' }}>
                                            <div
                                                className='button-footer'
                                                style={styles.buttonContainer}
                                            >
                                                <button
                                                    style={styles.buttonMarginRight}
                                                    type='submit'
                                                    className='button primary'
                                                    disabled={!canTerminate()}
                                                >
                                                    Terminate
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button hollow'
                                                    data-dismiss='modal'
                                                    onClick={_handleClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default TerminateMasterNoteModal;
